import React from 'react';
import { Eye, Mail, Pill, UserMinus, UserPlus } from 'lucide-react';

type MedcallMobileParticipant = {
  active?: boolean | null;
  aliases?: Array<{
    firstname?: string | null;
    lastname?: string | null;
  }> | null;
  cid?: string | null;
  created_at?: string | null;
  dob?: string | null;
  firstname?: string | null;
  id: number;
  lastname?: string | null;
  participant_id?: number | null;
  program?: string | null;
  view_user_ids?: string[] | null;
};

type MedcallMobileClientListProps = {
  canManageMedication: (participant: MedcallMobileParticipant) => boolean;
  currentUserId: number;
  emptyText?: string;
  loading: boolean;
  onAddMedication: (participant: MedcallMobileParticipant) => void;
  onAddToMyClients: (participant: MedcallMobileParticipant) => void;
  onOpen: (participant: MedcallMobileParticipant) => void;
  onRemoveFromMyClients: (participant: MedcallMobileParticipant) => void;
  onSendMessage: (participant: MedcallMobileParticipant) => void;
  participants: MedcallMobileParticipant[];
  pendingParticipantId: number | null;
  listWritePending?: boolean;
  scope: 'all' | 'my';
};

const normalizeValue = (value: unknown) => `${value || ''}`.trim();

const formatDate = (value: string | null | undefined) => {
  const match = normalizeValue(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return normalizeValue(value) || '—';
  }

  return `${match[2]}/${match[3]}/${match[1]}`;
};

const formatDateTime = (value: string | null | undefined) => {
  const parsed = new Date(normalizeValue(value));
  if (Number.isNaN(parsed.getTime())) {
    return normalizeValue(value) || '—';
  }

  return new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    month: '2-digit',
    timeZone: 'America/Los_Angeles',
    year: '2-digit',
  }).format(parsed).replace(',', '');
};

const formatAliases = (participant: MedcallMobileParticipant) => {
  if (!Array.isArray(participant.aliases)) {
    return '';
  }

  return participant.aliases
    .map((alias) => [alias.firstname, alias.lastname].filter(Boolean).join(' ').trim())
    .filter(Boolean)
    .join(', ');
};

const MobileInfoField = ({ label, value }: { label: string; value: React.ReactNode }) => {
  return (
    <div className="medcall-mobile-client__info-item">
      <span className="medcall-mobile-client__info-label">{label}</span>
      <span className="medcall-mobile-client__info-value">{value || '—'}</span>
    </div>
  );
};

const MobileAction = ({
  children,
  disabled = false,
  id,
  label,
  onClick,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  id: string;
  label: string;
  onClick: () => void;
}) => {
  return (
    <button
      aria-label={label}
      className="medcall-mobile-client__action"
      disabled={disabled}
      id={id}
      onClick={onClick}
      title={label}
      type="button"
    >
      {children}
    </button>
  );
};

export const MedcallMobileClientList = ({
  canManageMedication,
  currentUserId,
  emptyText = 'No clients found',
  loading,
  onAddMedication,
  onAddToMyClients,
  onOpen,
  onRemoveFromMyClients,
  onSendMessage,
  participants,
  pendingParticipantId,
  listWritePending = false,
  scope,
}: MedcallMobileClientListProps) => {
  if (loading && participants.length < 1) {
    return <div className="medcall-mobile-client-list__message">Loading clients...</div>;
  }

  if (participants.length < 1) {
    return <div className="medcall-mobile-client-list__message">{emptyText}</div>;
  }

  return (
    <div className="medcall-mobile-client-list" data-testid="medcall-mobile-client-list">
      {participants.map((participant) => {
        const aliases = formatAliases(participant);
        const isPending = listWritePending || pendingParticipantId === participant.id;
        const isOnMyClients = (participant.view_user_ids || []).includes(`${currentUserId}`);
        return (
          <article
            key={participant.id}
            className={`medcall-mobile-client${participant.active ? '' : ' medcall-mobile-client--inactive'}`}
          >
            <header className="medcall-mobile-client__header">
              <div className="medcall-mobile-client__title">
                <strong>{normalizeValue(participant.firstname)} {normalizeValue(participant.lastname)}</strong>
                {aliases ? <span>{aliases}</span> : null}
              </div>

              <div className="medcall-mobile-client__actions">
                <MobileAction
                  id={`medcall-mobile-client-${participant.id}-view`}
                  label="View CLient"
                  onClick={() => onOpen(participant)}
                >
                  <Eye aria-hidden="true" />
                </MobileAction>

                {scope === 'all' ? (
                  <MobileAction
                    disabled={isOnMyClients || isPending}
                    id={`medcall-mobile-client-${participant.id}-add-to-my-clients`}
                    label="Add To My Clients"
                    onClick={() => onAddToMyClients(participant)}
                  >
                    <UserPlus aria-hidden="true" />
                  </MobileAction>
                ) : null}

                {scope === 'my' && canManageMedication(participant) ? (
                  <MobileAction
                    id={`medcall-mobile-client-${participant.id}-add-medication`}
                    label="Add Medication"
                    onClick={() => onAddMedication(participant)}
                  >
                    <Pill aria-hidden="true" />
                  </MobileAction>
                ) : null}

                {scope === 'my' ? (
                  <MobileAction
                    disabled={isPending}
                    id={`medcall-mobile-client-${participant.id}-remove-from-my-clients`}
                    label="Remove From My Clients"
                    onClick={() => onRemoveFromMyClients(participant)}
                  >
                    <UserMinus aria-hidden="true" />
                  </MobileAction>
                ) : null}

                {scope === 'my' ? (
                  <MobileAction
                    id={`medcall-mobile-client-${participant.id}-send-message`}
                    label="Send Message"
                    onClick={() => onSendMessage(participant)}
                  >
                    <Mail aria-hidden="true" />
                  </MobileAction>
                ) : null}
              </div>
            </header>

            <div className="medcall-mobile-client__info">
              <MobileInfoField label="ID" value={participant.participant_id} />
              <MobileInfoField label="CID" value={participant.cid} />
              <MobileInfoField label="DOB" value={formatDate(participant.dob)} />
              <MobileInfoField label="Program" value={participant.program} />
              <MobileInfoField label="Added at" value={formatDateTime(participant.created_at)} />
            </div>
          </article>
        );
      })}
    </div>
  );
};
